import { useEffect, useState } from 'react';
import { Trash2, ToggleLeft, ToggleRight, Brain } from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import { promptExamplesApi, type PromptExample } from '../../lib/reportTemplatesApi';
import Alert from './Alert';
import ConfirmDialog from './ConfirmDialog';

export default function KiBibliothekTab() {
  const { darkMode } = useDarkMode();
  const [examples, setExamples] = useState<PromptExample[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: number; title: string } | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await promptExamplesApi.listAll();
      setExamples(data);
    } catch {
      setError('Fehler beim Laden der Bibliothek');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleToggle = async (id: number, current: boolean) => {
    await promptExamplesApi.toggle(id, !current);
    setExamples(prev => prev.map(e => e.id === id ? { ...e, isActive: !current } : e));
  };

  const handleDelete = (id: number, title: string) => {
    setDeleteConfirm({ id, title });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    const { id } = deleteConfirm;
    setDeleteConfirm(null);
    await promptExamplesApi.delete(id);
    setExamples(prev => prev.filter(e => e.id !== id));
  };

  const base = darkMode ? 'bg-slate-800 text-slate-100' : 'bg-white text-slate-900';
  const border = darkMode ? 'border-slate-700' : 'border-slate-200';

  return (
    <div className={`p-4 md:p-6 ${base}`}>
      <div className="flex items-center gap-3 mb-6">
        <Brain className="w-5 h-5 text-purple-500" />
        <h2 className="text-lg font-semibold">KI-Lernbibliothek</h2>
        <span className={`text-sm px-2 py-0.5 rounded-full ${darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
          {examples.filter(e => e.isActive).length} aktiv / {examples.length} gesamt
        </span>
      </div>

      {error && <Alert type="error" className="mb-4">{error}</Alert>}
      {loading && <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Wird geladen…</p>}

      {!loading && examples.length === 0 && (
        <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
          Noch keine Lernbeispiele vorhanden. Klicke im Tab „Berichte" auf das Brain-Symbol bei einem Bericht.
        </p>
      )}

      {examples.length > 0 && (
        <div className={`border ${border} rounded-lg overflow-x-auto`}>
          <table className="w-full min-w-[720px] text-sm">
            <thead className={darkMode ? 'bg-slate-700' : 'bg-slate-50'}>
              <tr>
                <th className="px-4 py-2 text-left font-medium">Abschnitt</th>
                <th className="px-4 py-2 text-left font-medium">Kunde</th>
                <th className="px-4 py-2 text-left font-medium">Branche</th>
                <th className="px-4 py-2 text-left font-medium">Datum</th>
                <th className="px-4 py-2 text-left font-medium">Aktiv</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {examples.map((e, i) => (
                <tr key={e.id} className={`border-t ${border} ${i % 2 === 0 ? '' : darkMode ? 'bg-slate-700/30' : 'bg-slate-50/50'}`}>
                  <td className="px-4 py-2 font-mono text-xs">{e.sectionTitle || e.cardId}</td>
                  <td className="px-4 py-2">{e.customerName || '—'}</td>
                  <td className="px-4 py-2 text-xs">{e.branche || e.wzCode || '—'}</td>
                  <td className="px-4 py-2 text-xs">{new Date(e.createdAt).toLocaleDateString('de-DE')}</td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => handleToggle(e.id, e.isActive)}
                      className={e.isActive ? 'text-green-500' : 'text-slate-400'}
                      title={e.isActive ? 'Deaktivieren' : 'Aktivieren'}
                    >
                      {e.isActive ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                    </button>
                  </td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => handleDelete(e.id, e.sectionTitle || e.cardId)}
                      className="text-red-400 hover:text-red-600 transition-colors"
                      title="Löschen"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={deleteConfirm !== null}
        title="Beispiel löschen"
        message={deleteConfirm ? `Beispiel "${deleteConfirm.title}" wirklich löschen?` : ''}
        confirmLabel="Löschen"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  );
}
